export const VNV_LINKED_INSTANCE = "P1S V&V";

/**
 * Attributes shown only on V&V equipment. Both are sourced from the SAP P1S V&V flow, so on any
 * other instrument they would render as a permanent "-".
 */
export const VNV_ONLY_ATTRIBUTE_KEYS = ["vnvStatus", "configurationDetails"];

/**
 * Attributes pulled to the front on V&V equipment, in this order. 
 */
export const VNV_LEADING_ATTRIBUTE_KEYS = ["vnvStatus", "serialNumber"];

/**
 * Attributes hidden on V&V equipment. P1S V&V records carry neither a nickname nor a manufacturer,
 * so both columns would be permanently empty.
 */
export const VNV_HIDDEN_ATTRIBUTE_KEYS = ["equipmentNickName", "manufacturer"];

/**
 * `entryType` value for a cluster. Kept as a literal rather than importing `ENTRY_TYPE` so this
 * module stays free of the `constants.js` import graph, which pulls in 21k icon modules.
 */
const CLUSTER_ENTRY_TYPE = "CLUSTER";

/** True when the record is a cluster. */
const isClusterEntry = (data) => data?.entryType === CLUSTER_ENTRY_TYPE;

/**
 * Both popover data sources deliver camelCase scalars: `getDigitalLabBookingInstrument` directly,
 * and the search index by way of `transformCloudSearchResults`. The popover renders with an empty
 * record while no equipment is selected, so a missing `linkedInstance` is expected, not an error.
 *
 * @param {object} data - An equipment record from either popover data source.
 * @returns {boolean} True when the record came from the SAP P1S V&V flow.
 */
export const isVnVEquipment = (data) => {
  const linkedInstance = data?.linkedInstance;
  if (typeof linkedInstance !== "string") {
    return false;
  }

  return linkedInstance.trim().toLowerCase() === VNV_LINKED_INSTANCE.toLowerCase();
};

/**
 * Drops the attribute keys that do not apply to this record and, on V&V equipment, hoists the
 * leading ones.
 *
 * A non-V&V record only ever has keys removed, never reordered, so its popover is identical to the
 * one that shipped before V&V Status existed.
 *
 * @param {string[]} metaKeys - Keys of the attribute meta being rendered, in declaration order.
 * @param {object} data - The equipment record the popover is showing.
 * @returns {string[]} The keys to render, in display order.
 */
export const visibleAttributeKeys = (metaKeys, data) => {
  // Clusters are out of scope for this card: the V&V view applies to standalone equipment only, so
  // a cluster renders its declared attributes untouched even when its linkedInstance says P1S V&V.
  if (isClusterEntry(data)) {
    return metaKeys;
  }

  if (!isVnVEquipment(data)) {
    return metaKeys.filter((key) => !VNV_ONLY_ATTRIBUTE_KEYS.includes(key));
  }

  const shown = metaKeys.filter((key) => !VNV_HIDDEN_ATTRIBUTE_KEYS.includes(key));
  const leading = VNV_LEADING_ATTRIBUTE_KEYS.filter((key) => shown.includes(key));

  return [...leading, ...shown.filter((key) => !leading.includes(key))];
};
